import { useState } from "react";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Button } from "../../ui/button";
import { Badge } from "../../ui/badge";
import { Slider } from "../../ui/slider";
import { RadioGroup, RadioGroupItem } from "../../ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { CollapsibleSection } from "../../ui/CollapsibleSection";
import { CuisineGrid } from "../CuisineGrid";
import { VoiceHint } from "../VoiceHint";
import { X, Plus, Clock, DollarSign, UtensilsCrossed, Heart } from "lucide-react";

interface DietaryProfile {
  diet: string;
  allergies: string[];
  avoids: string[];
  likes: string[];
  dislikes: string[];
  timePreference: number;
  budgetLevel: number;
}

interface EnhancedDietarySectionProps {
  profile: DietaryProfile;
  onUpdate: (updates: Partial<DietaryProfile>) => void;
  currentTier: string;
}

export function EnhancedDietarySection({ profile, onUpdate, currentTier }: EnhancedDietarySectionProps) {
  const [newAllergy, setNewAllergy] = useState("");
  const [newAvoid, setNewAvoid] = useState("");

  const dietOptions = [
    "No specific diet",
    "Vegetarian",
    "Vegan", 
    "Pescatarian",
    "Keto",
    "Paleo",
    "Mediterranean",
    "Low-carb",
    "Gluten-free",
    "Dairy-free"
  ];

  const addToken = (field: keyof DietaryProfile, value: string) => {
    if (!value.trim()) return;
    const currentArray = profile[field] as string[];
    if (!currentArray.includes(value.trim())) {
      onUpdate({ [field]: [...currentArray, value.trim()] });
    }
  };

  const removeToken = (field: keyof DietaryProfile, value: string) => {
    const currentArray = profile[field] as string[];
    onUpdate({ [field]: currentArray.filter(item => item !== value) });
  };

  // Calculate completion status for each section
  const isDietCompleted = Boolean(profile.diet && profile.diet !== "No specific diet");
  const isRestrictionsCompleted = profile.allergies.length > 0 || profile.avoids.length > 0;
  const isCuisineCompleted = profile.likes.length > 0;
  const isTimeCompleted = profile.timePreference !== 30; // 30 is default
  const isBudgetCompleted = profile.budgetLevel !== 50; // 50 is default

  return (
    <div className="space-y-6">
      <VoiceHint 
        message="Let's start with the basics! Tell me about your dietary preferences and I'll craft meals that fit your lifestyle perfectly."
        voice="medium"
      />

      {/* Primary Diet - Required */}
      <CollapsibleSection
        id="primary-diet"
        title="Primary Diet"
        subtitle="Your main dietary approach"
        completed={isDietCompleted}
        defaultOpen={!isDietCompleted}
        required={true}
      >
        <div className="space-y-3">
          <Label htmlFor="diet" className="text-sm font-medium text-gray-700">
            Select your diet type
          </Label>
          <Select value={profile.diet} onValueChange={(value) => onUpdate({ diet: value })}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Choose your primary diet" />
            </SelectTrigger>
            <SelectContent>
              {dietOptions.map((diet) => (
                <SelectItem key={diet} value={diet}>
                  {diet}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-gray-500">
            This helps us filter recipes that match your dietary needs.
          </p>
        </div>
      </CollapsibleSection>

      {/* Restrictions & Allergies */}
      <CollapsibleSection
        id="restrictions"
        title="Allergies & Restrictions"
        subtitle={`${profile.allergies.length + profile.avoids.length} items specified`}
        completed={isRestrictionsCompleted}
        defaultOpen={!isRestrictionsCompleted}
      >
        <div className="space-y-6">
          {/* Allergies */}
          <div className="space-y-3">
            <div className="flex items-center space-x-2">
              <UtensilsCrossed className="w-4 h-4 text-red-500" />
              <Label className="text-sm font-medium text-gray-700">
                Allergies & Strict Restrictions
              </Label>
            </div>
            <div className="flex gap-2">
              <Input
                placeholder="Add allergy (e.g., nuts, shellfish)"
                value={newAllergy}
                onChange={(e) => setNewAllergy(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addToken('allergies', newAllergy);
                    setNewAllergy("");
                  }
                }}
                className="flex-1"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  addToken('allergies', newAllergy);
                  setNewAllergy("");
                }}
                className="px-3"
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {profile.allergies.map((allergy) => (
                <Badge
                  key={allergy}
                  variant="outline"
                  className="bg-red-50 text-red-700 border-red-200 pr-1"
                >
                  {allergy}
                  <button
                    onClick={() => removeToken('allergies', allergy)}
                    className="ml-1 desktop-hover:text-red-900 p-1"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
          </div>

          {/* Ingredients to Avoid */}
          <div className="space-y-3">
            <Label className="text-sm font-medium text-gray-700">
              Ingredients to Avoid
            </Label>
            <p className="text-xs text-gray-500">
              Things you prefer not to eat (less strict than allergies)
            </p>
            <div className="flex gap-2">
              <Input
                placeholder="Add ingredient to avoid"
                value={newAvoid}
                onChange={(e) => setNewAvoid(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addToken('avoids', newAvoid);
                    setNewAvoid("");
                  }
                }}
                className="flex-1"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  addToken('avoids', newAvoid);
                  setNewAvoid("");
                }}
                className="px-3"
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {profile.avoids.map((avoid) => (
                <Badge
                  key={avoid}
                  variant="outline"
                  className="bg-coral/10 text-coral border-coral pr-1"
                >
                  {avoid}
                  <button
                    onClick={() => removeToken('avoids', avoid)}
                    className="ml-1 desktop-hover:text-warning/80 p-1"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
          </div>
        </div>
      </CollapsibleSection>

      {/* Cuisine Preferences */}
      <CollapsibleSection
        id="cuisine-preferences"
        title="Cuisine Preferences"
        subtitle={`${profile.likes.length} cuisines selected`}
        completed={isCuisineCompleted}
        defaultOpen={!isCuisineCompleted}
      >
        <div className="space-y-4">
          <div className="flex items-center space-x-2">
            <Heart className="w-4 h-4 text-warning" />
            <p className="text-sm text-gray-700">
              Choose cuisines you love to get personalized recommendations
            </p>
          </div>
          
          <CuisineGrid
            selectedCuisines={profile.likes}
            onSelectionChange={(cuisines) => onUpdate({ likes: cuisines })}
            maxSelections={5}
          />
        </div>
      </CollapsibleSection>

      {/* Cooking Time */}
      <CollapsibleSection
        id="cooking-time"
        title="Cooking Time Preference"
        subtitle={`${profile.timePreference} minutes`}
        completed={isTimeCompleted}
        defaultOpen={false}
      >
        <div className="space-y-4">
          <div className="flex items-center space-x-2">
            <Clock className="w-4 h-4 text-warning" />
            <Label className="text-sm font-medium text-gray-700">
              How much time do you typically have for cooking?
            </Label>
          </div>
          
          <RadioGroup
            value={profile.timePreference.toString()}
            onValueChange={(value) => onUpdate({ timePreference: parseInt(value) })}
            className="grid grid-cols-1 sm:grid-cols-2 gap-3"
          >
            {[
              { value: "20", label: "20 minutes", desc: "Quick & easy weeknight meals" },
              { value: "30", label: "30 minutes", desc: "Balanced cooking time" },
              { value: "45", label: "45 minutes", desc: "Moderate effort meals" },
              { value: "60", label: "60+ minutes", desc: "I love spending time cooking!" }
            ].map(({ value, label, desc }) => (
              <div key={value} className="flex items-center space-x-3 border rounded-lg p-4 desktop-hover:bg-gray-50 transition-colors">
                <RadioGroupItem value={value} id={`time-${value}`} />
                <div className="flex-1 min-w-0">
                  <Label htmlFor={`time-${value}`} className="text-sm font-medium text-gray-900 cursor-pointer">
                    {label}
                  </Label>
                  <p className="text-xs text-gray-600 mt-1">{desc}</p>
                </div>
              </div>
            ))}
          </RadioGroup>
        </div>
      </CollapsibleSection>

      {/* Budget Level */}
      <CollapsibleSection
        id="budget-level"
        title="Budget Preference"
        subtitle={profile.budgetLevel < 30 ? "Budget-friendly" : profile.budgetLevel < 70 ? "Moderate" : "Premium"}
        completed={isBudgetCompleted}
        defaultOpen={false}
      >
        <div className="space-y-4">
          <div className="flex items-center space-x-2">
            <DollarSign className="w-4 h-4 text-warning" />
            <Label className="text-sm font-medium text-gray-700">
              What's your typical grocery budget level?
            </Label>
          </div>
          
          <div className="space-y-3">
            <Slider
              value={[profile.budgetLevel]}
              onValueChange={(value) => onUpdate({ budgetLevel: value[0] })}
              max={100}
              step={10}
              className="w-full"
            />
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Budget-friendly</span>
              <span className="font-medium text-gray-900">
                {profile.budgetLevel < 30 ? "Low Budget" : 
                 profile.budgetLevel < 70 ? "Moderate Budget" : "Premium Budget"}
              </span>
              <span className="text-gray-500">Premium ingredients</span>
            </div>
            <p className="text-xs text-gray-500">
              This helps us suggest recipes with appropriate ingredient costs.
            </p>
          </div>
        </div>
      </CollapsibleSection>

      {/* Validation Hint */}
      {!isDietCompleted && !isCuisineCompleted && (
        <div className="mt-6">
          <VoiceHint 
            message="I need at least your diet type OR some cuisine preferences to get started with your meal planning!"
            voice="low"
            className="border-coral-200 bg-coral-50"
          />
        </div>
      )}
    </div>
  );
}